import React, { memo, useEffect, useMemo, useRef } from "react";

import { useSelector } from "react-redux";
import {
  StyledBotContainer,
  StyledFloatingButtonWrap,
} from "../Styles/StyledScreens";

import { HostWindowCommService } from "../../Services/IframeHostService/HostWindowCommService";
import { OEvents } from "../../Services/IframeHostService/models";
import { useAppDispatch, useAppSelector } from "../../Store";
import { useBotConfigStore } from "../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { allMessagesStore } from "../../Store/Slices/socket/AllMessagesSlice";
import {
  botInfoStore,
  setIsOpenedFirstTime,
  setOpenStatus,
} from "../../Store/Slices/socket/BotInfoSlice";
import { deviceSizeStore } from "../../Store/Slices/socket/DeviceSizeSlice";

import MainUI from "../../socket/screens/MainUI";
import useSocket from "../hooks/useSocket";
import { PreviewType, SOCKET_CONNECTION_TYPES } from "../utils/Enum";
import CalloutMessageScreen from "./CalloutMessageScreen";
import SpeedDialButton from "./SpeedDialButton";
import { socketStatusStore } from "../../Store/Slices/socket/SocketSlice";
import { AppConfig } from "../../Utils/app-config";

interface Props {}

const Home: React.FC<Props> = () => {
  const containerRef = useRef<any>(null);
  const floatingButtonRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const { botConfig } = useAppSelector(useBotConfigStore);

  const { chatOpened, isOpenedFirstTime, visitorId, botId, widgetPreviewType } =
    useSelector(botInfoStore);
  const { socket } = useAppSelector(socketStatusStore);

  const { sessionId } = useSelector(allMessagesStore);
  const { height, width } = useSelector(deviceSizeStore);

  let commService = useMemo(() => {
    return HostWindowCommService.getInstance();
  }, []);

  const handleQuickChat = () => {
    if (!chatOpened) {
      commService.postMessage({
        event_type: OEvents.CHATBOT_OPEN,
        data: { open: true },
      });
    }
    dispatch(setOpenStatus(true));
    dispatch(setIsOpenedFirstTime(true));
  };
  const handleOpenStateIframeDimensions = () => {
    const wh = {
      width: width,
      height: height,
    };
    commService.postMessage({
      event_type: OEvents.CHATBOT_RESIZE,
      data: wh,
    });
  };

  const handleClosedStateIframeDimensions = () => {
    if (floatingButtonRef?.current) {
      let { width: closedDivWidth, height: closedDivHeight } =
        floatingButtonRef.current.getBoundingClientRect();

      let newCalculatedWidth = closedDivWidth + 16 > 80 ? closedDivWidth : 80;
      let newCalculatedHeight =
        closedDivHeight + 16 > 84 ? closedDivHeight : 84;

      setTimeout(() => {
        const wh = {
          width: newCalculatedWidth,
          height: newCalculatedHeight,
        };
        commService.postMessage({
          event_type: OEvents.CHATBOT_RESIZE,
          data: wh,
        });
      }, 100);
    }
  };

  useEffect(() => {
    if (chatOpened && !isOpenedFirstTime) {
      dispatch(setIsOpenedFirstTime(true));
    }
    if (chatOpened) {
      handleOpenStateIframeDimensions();
    } else {
      handleClosedStateIframeDimensions();
    }
  }, [chatOpened, isOpenedFirstTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    connectSocketEvent,
    disconnectSocketEvent,
  } = useSocket();

  // ********************************************************

  // ─── REQUEST DATA EVENTS ──────────────────────────────────────────────────────────────

  // ********************************************************

  const BOT_HEADERS = {
    appid: JSON.stringify({
      botid: botId,
    }),
    "geta-host": AppConfig.getGetaHost(),
    visitor_id: visitorId || null,
    preview: widgetPreviewType === PreviewType.getaWidgetPreview ? true : false,
  };

  const sendRequestNewBotSessionEvent = () => {
    if (socket?.connected) {
      socket?.emit(
        SOCKET_CONNECTION_TYPES.INITIATE_BOT_SESSION,
        BOT_HEADERS,
      );
    } else {
      connectSocketEvent();
    }
  };

  const sendRequestUserReconnectEvent = () => {
    if (socket?.connected && sessionId) {
      let reconnectData = {
        session_id: sessionId,
        socket_id: socket?.id,
      };
      socket?.emit(
        SOCKET_CONNECTION_TYPES.USER_RECONNECT,
        reconnectData,
      );
    }
  };

  useEffect(() => {
    connectSocketEvent();

    return () => {
      disconnectSocketEvent();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (socket && socket?.connected && socket?.id) {
      if (sessionId && widgetPreviewType !== PreviewType.getaWidgetPreview) {
        // sendRequestUserReconnectEvent();
      } else {
        sendRequestNewBotSessionEvent();
      }
    }
  }, [socket?.connected, socket?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (widgetPreviewType === PreviewType.getaWidgetPreview) {
      setTimeout(() => {
        dispatch(setOpenStatus(true));
      }, 100); // DO NOT remove settimeout //
    }
  }, [widgetPreviewType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StyledBotContainer
      devicewidth={width}
      deviceheight={height}
      chatopened={chatOpened ? 1 : 0}
      id="bot-container"
      ref={containerRef}
      WIDGET_POSITION={botConfig.config?.display_position}
      isTooltipVisible={!isOpenedFirstTime}
    >
      {chatOpened ? <MainUI /> : null}

      {chatOpened ? null : (
        <StyledFloatingButtonWrap
          chatopened={chatOpened ? 1 : 0}
          tooltipPosition={botConfig.config?.tooltip?.position}
          WIDGET_POSITION={botConfig.config?.display_position}
          isTooltipVisible={isOpenedFirstTime}
          ref={floatingButtonRef}
        >
          {botConfig.config?.tooltip?.enabled &&
          !chatOpened &&
          !isOpenedFirstTime ? (
            <CalloutMessageScreen
              handleQuickChat={handleQuickChat}
              handleClosedStateIframeDimensions={
                handleClosedStateIframeDimensions
              }
            />
          ) : null}
          {chatOpened ? null : (
            <SpeedDialButton handleQuickChat={handleQuickChat} />
          )}
        </StyledFloatingButtonWrap>
      )}
    </StyledBotContainer>
  );
};

export default memo(Home);
